import React, { useState } from 'react';
import {
  Phone, PhoneOff, Delete, Loader2, PhoneIncoming, PhoneForwarded, UserPlus, Mail,
} from 'lucide-react';
import { apiPut } from '../../lib/api.js';
import * as tv from '../../lib/twilioVoice.js';
import LiveTranscriptPane from './LiveTranscriptPane.jsx';
import CallStatusCard from './CallStatusCard.jsx';
import CallerIdPicker from './CallerIdPicker.jsx';
import VoicemailDropSection from './VoicemailDropSection.jsx';
import ConferencePanel from './ConferencePanel.jsx';
import EmailComposer from './EmailComposer.jsx';
import AudioSettingsSection from './AudioSettingsSection.jsx';
import CopilotPanel from '../CopilotPanel.jsx';

const NUMPAD = [
  ['1', ''], ['2', 'ABC'], ['3', 'DEF'],
  ['4', 'GHI'], ['5', 'JKL'], ['6', 'MNO'],
  ['7', 'PQRS'], ['8', 'TUV'], ['9', 'WXYZ'],
  ['*', ''], ['0', '+'], ['#', ''],
];

/**
 * PhoneTab — The softphone dialer, keypad, in-call status card, and call history.
 */
export default function PhoneTab({
  dialInput,
  setDialInput,
  callState,
  isInCall,
  muted,
  callDuration,
  formatDuration,
  settings,
  drops = [],
  callerIdOpen,
  setCallerIdOpen,
  handleCall,
  handleHangUp,
  handleMute,
  handleVoicemailDrop,
  sdkBusy,
  activeLead,
  recentCalls = [],
  liveTurns = [],
  transcriptFading = false,
  activeCallId = null,
  refetchSettings,
}) {
  // In-call drawers: 'conference' | 'email' | null (one open at a time).
  const [drawer, setDrawer] = useState(null);
  const toggleDrawer = (name) => setDrawer((d) => (d === name ? null : name));
  const liveCall = callState === 'in-progress' || callState === 'ringing';

  function pad(key) {
    setDialInput((v) => v + key);
  }

  function backspace() {
    setDialInput((v) => v.slice(0, -1));
  }

  return (
    <div className="p-4 space-y-3">
      {/* Call status: ringing pill, lead / number, mute */}
      {isInCall && (
        <CallStatusCard
          callState={callState}
          activeLead={activeLead}
          muted={muted}
          callDuration={callDuration}
          formatDuration={formatDuration}
          onMute={handleMute}
        />
      )}

      {/* In-call actions: add a third party, email the lead */}
      {isInCall && liveCall && (
        <>
          <div className="grid grid-cols-2 gap-2">
            {[
              { id: 'conference', label: 'Add to Call', icon: UserPlus },
              { id: 'email', label: 'Compose Email', icon: Mail },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => toggleDrawer(id)}
                aria-pressed={drawer === id}
                className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold border transition ${
                  drawer === id
                    ? 'bg-civix-purple/20 border-civix-purple/50 text-civix-purpleSoft'
                    : 'bg-civix-panelLight border-civix-border text-white/70 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>
          {drawer === 'conference' && (
            <ConferencePanel callId={activeCallId} callState={callState} activeLead={activeLead} />
          )}
          {drawer === 'email' && (
            <EmailComposer
              leadId={activeLead?.leadId || null}
              leadEmail={activeLead?.email || ''}
              onSent={() => setDrawer(null)}
            />
          )}
        </>
      )}

      {/* Dedicated voicemail drop (ringing / connected) */}
      {(callState === 'ringing' || callState === 'in-progress') && (
        <VoicemailDropSection drops={drops} callState={callState} onDrop={handleVoicemailDrop} />
      )}

      {/* Live transcript & Whisper Copilot */}
      {(isInCall || liveTurns.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          <LiveTranscriptPane
            turns={liveTurns}
            active={isInCall && callState === 'in-progress'}
            fading={transcriptFading}
          />
          <CopilotPanel
            activeCallId={activeCallId}
            active={isInCall && callState === 'in-progress'}
          />
        </div>
      )}

      {/* Caller ID selector */}
      {!isInCall && (
        <CallerIdPicker
          settings={settings}
          open={callerIdOpen}
          setOpen={setCallerIdOpen}
          refetchSettings={refetchSettings}
        />
      )}

      {/* Take calls via mode selector */}
      {!isInCall && (
        <div className="space-y-1.5">
          <span className="text-white/40 text-xs">Take calls via:</span>
          <div className="flex gap-1.5">
            {[
              { value: 'browser', label: '\uD83C\uDF10 Browser', title: 'Receive bridged calls through the browser softphone' },
              { value: 'cell',    label: '\uD83D\uDCF1 Cell',    title: 'Receive bridged calls on your cell phone' },
              { value: 'both',    label: '\u2194\uFE0F Both',    title: 'Ring browser softphone and cell simultaneously — first to pick up wins' },
            ].map(({ value, label, title }) => (
              <button
                key={value}
                title={title}
                onClick={async () => {
                  try {
                    await apiPut('/phone/settings', { mode: value });
                    refetchSettings?.();
                  } catch {}
                }}
                className={`flex-1 py-1 rounded-lg text-[10px] font-medium transition border ${
                  (settings.mode || 'browser') === value
                    ? 'bg-civix-purple/20 text-civix-purpleSoft border-civix-purple/40'
                    : 'bg-transparent text-white/40 border-civix-border hover:text-white/70 hover:border-civix-border/60'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Dial input */}
      {!isInCall && (
        <div className="relative">
          <input
            type="tel"
            value={dialInput}
            onChange={(e) => setDialInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCall()}
            placeholder="Enter phone number…"
            className="w-full bg-civix-panelLight border border-civix-border rounded-xl px-4 py-3 text-center text-lg font-mono text-white placeholder:text-white/30 focus:outline-hidden focus:border-civix-purple transition"
          />
          {dialInput && (
            <button
              onClick={backspace}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition"
            >
              <Delete className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Numpad */}
      {!isInCall && (
        <div className="grid grid-cols-3 gap-1.5">
          {NUMPAD.map(([digit, letters]) => (
            <button
              key={digit}
              onClick={() => {
                if (callState === 'in-progress') {
                  tv.sendDigits(digit);
                } else {
                  pad(digit);
                }
              }}
              className="flex flex-col items-center justify-center py-2.5 rounded-xl bg-civix-panelLight border border-civix-border/60 hover:bg-civix-border/40 hover:border-civix-purple/40 transition active:scale-95"
            >
              <span className="text-white font-semibold text-base leading-none">{digit}</span>
              {letters && <span className="text-white/30 text-[9px] mt-0.5">{letters}</span>}
            </button>
          ))}
        </div>
      )}

      {/* Call / Hang-up button */}
      <div className="flex gap-2">
        {!isInCall ? (
          <button
            onClick={handleCall}
            disabled={!dialInput.trim() || sdkBusy}
            className="flex-1 py-3.5 rounded-xl bg-civix-purple hover:bg-civix-purpleDeep disabled:opacity-50 disabled:cursor-not-allowed font-bold text-white flex items-center justify-center gap-2 shadow-glow-purple transition"
          >
            {sdkBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Phone className="w-5 h-5" />}
            CALL
          </button>
        ) : (
          <button
            onClick={handleHangUp}
            className="flex-1 py-3.5 rounded-xl bg-red-500 hover:bg-red-600 font-bold text-white flex items-center justify-center gap-2 transition"
          >
            <PhoneOff className="w-5 h-5" /> Hang Up
          </button>
        )}
      </div>

      <AudioSettingsSection />

      {/* Recording indicator */}
      {!isInCall && (
        <div className="flex items-center justify-between px-1">
          <span className="text-xs text-white/40">Call Recording</span>
          <button
            onClick={async () => {
              try { await apiPut('/phone/settings', { recordCalls: !settings.recordCalls }); } catch {}
            }}
            className={`w-9 h-5 rounded-full transition-colors relative ${settings.recordCalls !== false ? 'bg-civix-purple' : 'bg-civix-border'}`}
          >
            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${settings.recordCalls !== false ? 'left-[18px]' : 'left-0.5'}`} />
          </button>
        </div>
      )}

      {/* Recent calls */}
      {!isInCall && recentCalls.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2 px-1">Recent Calls</div>
          <div className="space-y-1">
            {recentCalls
              .filter((c) => c.leadName !== 'Power Dial session'
                && !String(c.twilioCallSid || '').startsWith('pdsession_')
                && (c.direction === 'outbound' ? c.toNumber : c.fromNumber))
              .slice(0, 5).map((c) => (
              <button
                key={c._id}
                onClick={() => setDialInput(c.direction === 'outbound' ? c.toNumber : c.fromNumber)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-civix-panelLight transition text-left"
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                  c.direction === 'inbound' ? 'bg-civix-purple/20' : 'bg-green-500/20'
                }`}>
                  {c.direction === 'inbound' ? (
                    <PhoneIncoming className="w-3 h-3 text-civix-purpleSoft" />
                  ) : (
                    <PhoneForwarded className="w-3 h-3 text-green-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-xs font-medium truncate">
                    {c.leadId?.firstName ? `${c.leadId.firstName} ${c.leadId.lastName || ''}`.trim() : (c.direction === 'outbound' ? c.toNumber : c.fromNumber)}
                  </div>
                  <div className="text-white/40 text-[10px]">{c.disposition || c.status} · {c.durationSec ? `${Math.ceil(c.durationSec / 60)}m` : '—'}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
